import { memo, useEffect } from 'react';
import { FieldType, IField } from '@baishuyun/types';
import { FieldTypeNameMap } from '../const/ui';
import { isSubformFieldEqual, toHumanReadable } from '../lib/utils';
import { SubformFieldsValueChecker } from './subform-fields-value-checker';
import LineChecker from './line-checker';

type Field = IField;

export interface FieldValueCheckerProps {
  field: IField;
  isSubField?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  onChange?: (checked: boolean, field: Field, parentFieldName?: string, index?: number) => void;
  onLoaded?: (field: Field) => void;
  streaming?: boolean;
}

export const FieldValueChecker = memo(
  ({
    field,
    disabled,
    isSubField,
    readonly,
    streaming,
    onChange,
    onLoaded,
  }: FieldValueCheckerProps) => {
    const { widget, label } = field;
    if (!widget) {
      return null;
    }

    const { type, value: v } = widget;

    const t = type as FieldType;

    const value = toHumanReadable(v, t);

    if (!FieldTypeNameMap.hasOwnProperty(type)) {
      return null;
    }

    const isSubForm = type === 'subform';

    useEffect(() => {
      onLoaded?.(field);
    }, []);

    const handleCheck = (checked: boolean) => {
      onChange?.(checked, field);
    };

    const extraPadding = isSubField ? 'pl-6' : '';

    return (
      <>
        <LineChecker
          streaming={streaming}
          title={label}
          disabled={disabled}
          defaultChecked={true}
          onCheckedChange={handleCheck}
          className={extraPadding}
          readonly={readonly}
          shortDesc={isSubForm ? '' : (value as string)}
        />
        {isSubForm && (
          <SubformFieldsValueChecker
            readonly={readonly}
            subformField={field}
            onChange={(detail) => {
              onChange?.(detail.checked, detail.field, detail.parentFieldName, detail.row);
            }}
            streaming={streaming}
          />
        )}
      </>
    );
  },
  (p1, p2) => {
    // 标签一致
    const isLabelEqual = p1.field?.label === p2.field?.label;
    if (!isLabelEqual) {
      return false;
    }

    // id 一致
    const isFieldNameEqual = p1.field?.widget?.widgetName === p2.field?.widget?.widgetName;
    if (!isFieldNameEqual) {
      return false;
    }

    // 禁用状态一致
    const isDisabledEqual = p1.disabled === p2.disabled;
    if (!isDisabledEqual) {
      return false;
    }

    // 只读状态一致
    const isReadonlyEqual = p1.readonly === p2.readonly;
    if (!isReadonlyEqual) {
      return false;
    }

    // 流式状态一致
    const isStreamingEqual = p1.streaming === p2.streaming;
    if (!isStreamingEqual) {
      return false;
    }

    // 进一步比较子表单下的字段
    return isSubformFieldEqual(p1?.field, p2?.field);
  }
);
